import { Col, FormCheck, FormGroup, FormLabel, Row } from "react-bootstrap";
import InputText from "../../../components/Forms/Element/InputText";
import StatusCheckBox from "../../../components/Common/StatusCheckbox";
import { useTranslation } from "react-i18next";
import InputDecimal from "../../../components/Forms/Element/InputDecimal";
import TextArea from "../../../components/Forms/Element/TextArea";
import { useEffect, useState } from "react";
import { convertArrayBufferToBlob, convertStatusToBoolean, convertStringToStringDouble } from "../../../../utils/ConverterUtils";
import ImagePreview from "../../../components/Common/ImagePreview";
import Common from "../../../../constants/Common";
import { downloadUploadFile } from "../../../../services/AxiosService";
import Api from "../../../../constants/Api";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import CategorySelect from "../../../components/Custom/Main/Category/CategorySelect";
import { formatNumberWithTwoDecimalPlaces } from "../../../../utils/FormatUtils";
import VendorSelect from "../../../components/Custom/Main/Vendor/VendorSelect";
import BranchMultiSelect from "../../../components/Custom/Main/Branch/BranchMultiSelect";
import axiosInstance from "../../../../services/AxiosInstance";
import { ApiError } from "../../../components/Common/ApiError";
import LoadingIcon from "../../../components/Icon/LoadingIcon";
import InputNumeric from "../../../components/Forms/Element/InputNumeric";
import SegmentedButtons from "../../../components/Forms/Element/SegmentedButtons";
import FormSection from "../../../components/Common/Form/FormSection";
import HintTooltip from "../../../components/Common/HintTooltip";
import "./CatalogForm.css";

const CatalogForm = (props) => {
    const { t } = useTranslation();
    const {
        status, setStatus, formik, entityData, setUploadedFile,
        uploadedFile, setTrackStock, trackStock, setSelectAllBranches, selectAllBranches,
        isService = false } = props;
    const [showLoading, setShowLoading] = useState(false);
    const [showLoadingSelectAllBranches, setShowLoadingSelectAllBranches] = useState(false);

    // Campo vazio, zero ou texto inválido significam a mesma coisa para a agenda: não há
    // tempo cadastrado, e ela vai reservar a janela padrão.
    const hasDuration = Number(formik.values.duration) > 0;
    useEffect(() => {
        if (isService) {
            // Serviços nunca devem habilitar movimentação de estoque.
            setTrackStock(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isService]);

    // Function to calculate the suggested price
    const calculateSuggestedPrice = () => {
        const costPrice = parseFloat(!formik.values.costPrice ? 0 : convertStringToStringDouble(formik.values.costPrice));
        const icms = parseFloat(!formik.values.icms ? 0 : convertStringToStringDouble(formik.values.icms));
        const otherTaxes = parseFloat(!formik.values.otherTaxes ? 0 : convertStringToStringDouble(formik.values.otherTaxes));
        const extraCosts = parseFloat(!formik.values.extraCosts ? 0 : convertStringToStringDouble(formik.values.extraCosts));
        const profitMargin = parseFloat(!formik.values.profitMargin ? 0 : convertStringToStringDouble(formik.values.profitMargin));

        // Calcula o custo final com impostos
        let costWithTaxes = costPrice * (1 + icms / 100);
        costWithTaxes = costWithTaxes * (1 + otherTaxes / 100);

        // Adiciona os custos extras antes de aplicar a margem de lucro
        costWithTaxes += extraCosts;

        // Calcula o preço sugerido com base na margem de lucro
        const calculatedPrice = costWithTaxes * (1 + profitMargin / 100);
        formik.setFieldValue('suggestedPrice', formatNumberWithTwoDecimalPlaces(calculatedPrice));
    };

    useEffect(() => {
        // Preenche os valores nos campos.
        if (entityData != null) {
            formik.setFieldValue('name', entityData.name);
            formik.setFieldValue('price', formatNumberWithTwoDecimalPlaces(entityData.price));
            formik.setFieldValue('priceFrom', entityData.priceFrom === true);
            // Ausente significa "aparece": cadastro anterior ao campo não pode sumir do site.
            formik.setFieldValue('showInPublicBooking', entityData.showInPublicBooking !== false);
            formik.setFieldValue('requiresDeposit', entityData.requiresDeposit === true);
            formik.setFieldValue('depositType', entityData.depositType || 'AMOUNT');
            formik.setFieldValue('depositValue', formatNumberWithTwoDecimalPlaces(entityData.depositValue || 0));
            formik.setFieldValue('detail', entityData.detail);
            formik.setFieldValue('barCode', entityData.barCode);
            formik.setFieldValue('refCode', entityData.refCode);
            formik.setFieldValue('icms', formatNumberWithTwoDecimalPlaces(entityData.icms));
            formik.setFieldValue('costPrice', formatNumberWithTwoDecimalPlaces(entityData.costPrice));
            formik.setFieldValue('otherTaxes', formatNumberWithTwoDecimalPlaces(entityData.otherTaxes));
            formik.setFieldValue('extraCosts', formatNumberWithTwoDecimalPlaces(entityData.extraCosts));
            formik.setFieldValue('profitMargin', formatNumberWithTwoDecimalPlaces(entityData.profitMargin));
            formik.setFieldValue('suggestedPrice', formatNumberWithTwoDecimalPlaces(entityData.suggestedPrice));
            formik.setFieldValue('repurchaseIntervalDays', entityData.repurchaseIntervalDays ?? '');
            // Serviço legado sem tempo abre com zero, o mesmo padrão do cadastro novo, para
            // o campo nunca ficar vazio e exigir uma decisão de quem está editando.
            formik.setFieldValue('duration', entityData.duration ?? (isService ? 0 : ''));

            // Preenche a categoria do item
            if (entityData.category) {
                formik.setFieldValue('category', {
                    value: entityData.category.id,
                    label: entityData.category.name
                });
            }

            // Preenche o select
            if (entityData.branches && entityData.branches.length > 0) {
                let branches = [];
                entityData.branches.forEach(branch => {
                    branches.push(
                        { value: branch.id, label: branch.name }
                    );
                });
                formik.setFieldValue('branches', branches);
            }

            if (entityData.vendor !== null) {
                formik.setFieldValue('vendor', {
                    value: entityData.vendor.id,
                    label: entityData.vendor.name
                });
            }

            // Se possuir imagem cadastrada, exibe-a na tela.
            if (entityData.uploadedFileLocation !== null) {
                setShowLoading(true)
                downloadUploadFile(Api.main.product + "/download", entityData.id)
                    .then(resp => {
                        if (resp.data) {
                            setUploadedFile(convertArrayBufferToBlob(resp.data));
                        }
                    })
                    .catch(() => {
                        // Arquivo não encontrado no S3 (ex.: registro antigo) - não trava o usuário com erro.
                    })
                    .finally(() => setShowLoading(false));
            }

            setStatus(convertStatusToBoolean(entityData.status));
            setTrackStock(entityData.trackStock);

            // Regras
        }

        // eslint-disable-next-line
    }, [entityData]);

    useEffect(() => {
        if (selectAllBranches) {
            setShowLoadingSelectAllBranches(true);
            axiosInstance.get(Api.main.branch + "/user")
                .then((resp) => {
                    let data = resp.data;
                    if (data.length > 0) {
                        let branches = [];
                        data.forEach((branch) => {
                            branches.push(
                                {
                                    value: branch.id,
                                    label: branch.name
                                }
                            );
                        });
                        formik.setFieldValue('branches', branches);
                    }
                })
                .catch(error => ApiError(error))
                .finally(() => setShowLoadingSelectAllBranches(false));
        } else {
            formik.setFieldValue('branches', []);
        }
    }, [selectAllBranches])

    useEffect(() => {
        calculateSuggestedPrice();
    }, [formik.values.costPrice, formik.values.icms, formik.values.otherTaxes, formik.values.extraCosts, formik.values.costPrice, formik.values.profitMargin]);

    return (
        <>
            <div className="form-layout form-layout--spaced">
                <FormSection
                    title={t('basic_information')}
                    description={t('product_basic_information_hint')}
                    icon="fa-solid fa-box"
                >
                    <Row className="g-3">
                        <Col md={12} xl={6}>
                            <FormGroup>
                                <FormLabel>{t('name')} <AsteriskRequired /></FormLabel>
                                <InputText
                                    name="name"
                                    placeholder={t('product_name_placeholder')}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.name}
                                    error={formik.errors.name}
                                    maxlength="50"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6} xl={4}>
                            <FormLabel>
                                {t('category')} <AsteriskRequired />
                                <HintTooltip text={t('product_category_hint')} />
                            </FormLabel>
                            <CategorySelect
                                name="category"
                                value={formik.values.category}
                                onChange={(value) =>
                                    formik.setFieldValue('category', value)
                                }
                                onBlur={formik.handleBlur}
                                error={formik.errors.category} />
                        </Col>
                        <Col md={6} xl={2}>
                            <FormGroup>
                                <FormLabel>{t('price')} ({t('currency_symbol')}) <AsteriskRequired /></FormLabel>
                                <InputDecimal
                                    name="price"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.price}
                                    error={formik.errors.price} />
                            </FormGroup>
                        </Col>

                        <Col md={12}>
                            <FormLabel>{t('message.product_allowed_branches')} <AsteriskRequired /></FormLabel>
                            <BranchMultiSelect
                                name="branches"
                                value={formik.values.branches}
                                onChange={(value) =>
                                    formik.setFieldValue('branches', value)
                                }
                                onBlur={formik.handleBlur}
                                error={formik.errors.branches} />
                        </Col>
                        <Col md={12}>
                            <FormCheck className="d-flex align-items-center">
                                <input
                                    id="selectAllBranches"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={selectAllBranches}
                                    onClick={() => setSelectAllBranches(!selectAllBranches)}
                                />
                                <FormLabel
                                    htmlFor="selectAllBranches"
                                    className="form-check-label ms-2 mb-0"
                                >
                                    {t('select_all_branches')}
                                </FormLabel>
                                {
                                    (showLoadingSelectAllBranches) ?
                                        <span className="ms-2">
                                            <LoadingIcon />{" "}{t('message.looking')}
                                        </span>
                                        : ''
                                }
                            </FormCheck>
                        </Col>
                    </Row>
                </FormSection>

                {/* Seção própria porque as duas opções respondem à mesma pergunta — como este
                    serviço aparece para quem marca — e cada uma precisa de uma linha
                    explicando o efeito. Espremidas ao lado do preço, viravam dois interruptores
                    sem contexto, e o operador só descobria o que faziam testando. */}
                {isService && (
                    <FormSection
                        title={t('service_display_title')}
                        description={t('service_display_hint')}
                        icon="fa-solid fa-eye"
                    >
                        <div className="catalog-form__options">
                            <label className="catalog-form__option" htmlFor="priceFrom">
                                <div className="catalog-form__option-text">
                                    <span className="catalog-form__option-title">{t('price_from_label')}</span>
                                    <span className="catalog-form__option-hint">{t('price_from_option_hint')}</span>
                                </div>
                                <FormCheck
                                    type="switch"
                                    id="priceFrom"
                                    checked={!!formik.values.priceFrom}
                                    onChange={(event) => formik.setFieldValue('priceFrom', event.target.checked)} />
                            </label>

                            <label className="catalog-form__option" htmlFor="showInPublicBooking">
                                <div className="catalog-form__option-text">
                                    <span className="catalog-form__option-title">{t('show_in_public_booking_label')}</span>
                                    <span className="catalog-form__option-hint">{t('show_in_public_booking_option_hint')}</span>
                                </div>
                                <FormCheck
                                    type="switch"
                                    id="showInPublicBooking"
                                    checked={formik.values.showInPublicBooking !== false}
                                    onChange={(event) => formik.setFieldValue('showInPublicBooking', event.target.checked)} />
                            </label>
                        </div>
                    </FormSection>
                )}

                {/* Reserva é decisão de serviço caro: seção própria, para o interruptor ter
                    contexto e o tipo e o valor só aparecerem quando a exigência está ligada. */}
                {isService && (
                    <FormSection
                        title={t('deposit_section_title')}
                        description={t('deposit_section_hint')}
                        icon="fa-solid fa-shield-halved"
                    >
                        <div className="catalog-form__options">
                            <label className="catalog-form__option" htmlFor="requiresDeposit">
                                <div className="catalog-form__option-text">
                                    <span className="catalog-form__option-title">{t('deposit_required_label')}</span>
                                    <span className="catalog-form__option-hint">{t('deposit_required_hint')}</span>
                                </div>
                                <FormCheck
                                    type="switch"
                                    id="requiresDeposit"
                                    checked={!!formik.values.requiresDeposit}
                                    onChange={(event) => formik.setFieldValue('requiresDeposit', event.target.checked)} />
                            </label>
                        </div>

                        {formik.values.requiresDeposit && (
                            <Row className="mt-3">
                                <Col md={6} className="mb-3">
                                    <FormLabel>{t('deposit_type_label')}</FormLabel>
                                    <SegmentedButtons
                                        compact
                                        ariaLabel={t('deposit_type_label')}
                                        value={formik.values.depositType}
                                        onChange={(value) => formik.setFieldValue('depositType', value)}
                                        options={[
                                            { value: 'AMOUNT', label: t('deposit_type_amount') },
                                            { value: 'PERCENT', label: t('deposit_type_percent') }
                                        ]} />
                                </Col>
                                <Col md={6} className="mb-3">
                                    <FormGroup>
                                        <FormLabel>
                                            {t('deposit_value_label')}
                                            <HintTooltip text={t('deposit_value_hint')} />
                                        </FormLabel>
                                        <InputDecimal
                                            name="depositValue"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.depositValue}
                                            error={formik.errors.depositValue} />
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}
                    </FormSection>
                )}

                <FormSection
                    title={t('optional_fields')}
                    description={t('product_additional_information_hint')}
                    icon="fa-solid fa-info-circle"
                >
                    {!isService && (
                        <Row className="mb-3">
                            <Col md={4}>
                                <FormLabel>
                                    {t('bar_code')}
                                    <HintTooltip text={t('product_bar_code_hint')} />
                                </FormLabel>
                                <InputText
                                    name="barCode"
                                    value={formik.values.barCode}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    maxLength="100" />
                            </Col>
                            <Col md={4}>
                                <FormLabel>
                                    {t('ref_code')}
                                    <HintTooltip text={t('product_ref_code_hint')} />
                                </FormLabel>
                                <InputText
                                    name="refCode"
                                    value={formik.values.refCode}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    maxLength="20" />
                            </Col>
                            <Col md={4}>
                                <FormLabel>{t('vendor')}</FormLabel>
                                <VendorSelect
                                    name="vendor"
                                    value={formik.values.vendor}
                                    onChange={(value) =>
                                        formik.setFieldValue('vendor', value)
                                    }
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.vendor} />
                            </Col>
                        </Row>
                    )}
                    {/* Tempo de execução, comissão e retorno ficam juntos por serem os números
                        do dia a dia do item. Sem o tempo, o produto divide a linha em dois. */}
                    <Row className="mb-3">
                        {/* Só o serviço ocupa janela na agenda; produto não tem tempo de execução. */}
                        {isService && (
                            <Col md={4}>
                                <FormGroup>
                                    <FormLabel>
                                        {t('service_duration_minutes')}
                                        <AsteriskRequired />
                                        <HintTooltip text={t('service_duration_hint')} />
                                    </FormLabel>
                                    <InputNumeric
                                        name="duration"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.duration}
                                        error={formik.errors.duration}
                                        placeholder={t('service_duration_placeholder')} />
                                    {/* Serviço antigo, salvo antes de o campo virar obrigatório:
                                        explica por que a agenda está reservando 30 minutos e o
                                        que acontece ao salvar. O erro de validação só aparece
                                        depois que o formulário é submetido. */}
                                    {!formik.errors.duration && !hasDuration && (
                                        <small className="catalog-form__duration-warning">
                                            <i className="fas fa-triangle-exclamation"></i>
                                            {t('service_duration_missing_warning')}
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>
                        )}
                        <Col md={isService ? 4 : 6}>
                            <FormGroup>
                                <FormLabel>
                                    {isService ? t('return_interval_days') : t('repurchase_interval_days')}
                                    {/* O mesmo campo possui uma explicação contextual para cada tipo do catálogo. */}
                                    <HintTooltip text={isService
                                        ? t('service_return_interval_hint')
                                        : t('product_repurchase_interval_hint')} />
                                </FormLabel>
                                <InputNumeric
                                    name="repurchaseIntervalDays"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.repurchaseIntervalDays}
                                    error={formik.errors.repurchaseIntervalDays}
                                    placeholder={t('repurchase_interval_placeholder')} />
                            </FormGroup>
                        </Col>
                    </Row>
                    {/* A descrição fica sozinha na linha para aproveitar a largura total do form. */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <FormLabel>{t('details')}</FormLabel>
                            <TextArea name="detail" style={{ height: "100px" }}
                                maxLength={500}
                                value={formik.values.detail}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                counter="true"
                            />
                        </Col>
                    </Row>
                </FormSection>

                {!isService && (
                    <FormSection
                        title={t('costs_and_margins')}
                        description={t('product_costs_hint')}
                        icon="fa-solid fa-calculator"
                    >
                        <Row className="g-3">
                            <Col md={6} xl={2}>
                                <FormLabel>
                                    {t('cost_price')} ({t('currency_symbol')})
                                    <HintTooltip text={t('product_cost_price_hint')} />
                                </FormLabel>
                                <InputDecimal
                                    name="costPrice"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.costPrice}
                                    error={formik.errors.costPrice} />
                            </Col>
                            <Col md={6} xl={2}>
                                <FormLabel>
                                    {t('icms')} (%)
                                    <HintTooltip text={t('product_icms_hint')} />
                                </FormLabel>
                                <InputDecimal
                                    name="icms"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.icms}
                                    error={formik.errors.icms} />
                            </Col>
                            <Col md={6} xl={2}>
                                <FormGroup>
                                    <FormLabel>
                                        {t('other_taxes')} (%)
                                        <HintTooltip text={t('product_other_taxes_hint')} />
                                    </FormLabel>
                                    <InputDecimal
                                        name="otherTaxes"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.otherTaxes}
                                        error={formik.errors.otherTaxes} />
                                </FormGroup>
                            </Col>
                            <Col md={6} xl={2}>
                                <FormGroup>
                                    <FormLabel>
                                        {t('extra_costs')} ({t('currency_symbol')})
                                        <HintTooltip text={t('product_extra_costs_hint')} />
                                    </FormLabel>
                                    <InputDecimal
                                        name="extraCosts"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.extraCosts}
                                        error={formik.errors.extraCosts} />
                                </FormGroup>
                            </Col>
                            <Col md={6} xl={2}>
                                <FormGroup>
                                    <FormLabel>
                                        {t('profit_margin')} (%)
                                        <HintTooltip text={t('product_profit_margin_hint')} />
                                    </FormLabel>
                                    <InputDecimal
                                        name="profitMargin"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.profitMargin}
                                        error={formik.errors.profitMargin} />
                                </FormGroup>
                            </Col>
                            <Col md={6} xl={2}>
                                <FormGroup>
                                    <FormLabel>
                                        <strong>{t('suggested_price')} ({t('currency_symbol')})</strong>
                                        <HintTooltip text={t('product_suggested_price_hint')} />
                                    </FormLabel>
                                    <InputDecimal
                                        name="suggestedPrice"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.suggestedPrice}
                                        error={formik.errors.suggestedPrice}
                                        disabled={true} />
                                </FormGroup>
                            </Col>
                        </Row>
                    </FormSection>
                )}

                <FormSection
                    title={t('picture')}
                    description={t('product_image_hint')}
                    icon="fa-solid fa-image"
                >
                    <Row>
                        <Col md={12}>
                            <ImagePreview
                                id="file"
                                permittedWidth={Common.IMAGE_PERMITTED_WIDTH}
                                permittedHeight={Common.IMAGE_PERMITTED_HEIGHT}
                                permittedSize={Common.IMAGE_PERMITTED_FILE_SIZE}
                                setUploadedFile={setUploadedFile}
                                uploadedFile={uploadedFile}
                                showLoading={showLoading}
                            />
                        </Col>
                    </Row>
                </FormSection>

                {!isService && (
                    <FormSection
                        title={t('track_stock')}
                        description={t('product_stock_control_hint')}
                        icon="fa-solid fa-boxes"
                    >
                        <Row className="g-3">
                            <Col md={12}>
                                <FormCheck>
                                    <input
                                        id="stockControl"
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={trackStock}
                                        onClick={() => setTrackStock(!trackStock)}
                                    />
                                    <FormLabel
                                        htmlFor="stockControl"
                                        className="form-check-label"
                                    >
                                        {t('stock_control')}
                                    </FormLabel>
                                </FormCheck>
                            </Col>
                            <Col md={10}>
                                <small className="text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    {t('stock_control_info')}
                                </small>
                            </Col>
                        </Row>
                    </FormSection>
                )}

                <FormSection
                    title={t('status')}
                    description={t('product_status_hint')}
                    icon="fa-solid fa-toggle-on"
                    compact
                >
                    <Row>
                        <Col md={12}>
                            <FormGroup className="mb-0">
                                <StatusCheckBox
                                    name="status"
                                    defaultChecked={status}
                                    checked={status}
                                    onClick={() => setStatus(!status)} />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>
            </div>
        </>
    );
}

export default CatalogForm;
